import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { TestLocationDetail } from "../../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import WarningIcon from "../../../svg/warningIcon";
import SingleInputField from "./singleInputField";
import { applySiteDetailsAutoPopulate } from "./siteDetailsAutoPopulateHelper";
import {
  SiteDetailsFieldOptions,
  SITE_DETAILS_FIELDS,
} from "./siteDetailsForm.config";
import {
  blankIfMissingSiteDetailsPlaceholder,
  getSiteDetailsFieldError,
  isInternalSiteType,
  SiteDetailsFormValues,
  SITE_DETAILS_MISSING_FIELD_PLACEHOLDER,
} from "./siteDetailsForm.types";

/** Address fields that show "-" when empty, without storing that placeholder. */
const ADDRESS_DISPLAY_FIELDS = new Set<keyof SiteDetailsFormValues>([
  "address",
  "city",
  "state",
  "zip",
]);

/** Props for the site details form. */
export interface SiteDetailsFormProps {
  /** Current form values. */
  values: SiteDetailsFormValues;
  /** Emits the full updated form when any field changes. */
  onChange: (values: SiteDetailsFormValues) => void;
  /** Optional dropdown options per field. */
  options?: SiteDetailsFieldOptions;
  /** Cached test locations used to auto-populate sibling fields. */
  locations?: TestLocationDetail[];
  /** When true, shows loading on each searchable field while the catalog loads. */
  loading?: boolean;
  /** Whether the site has multiple addresses. */
  hasMultipleAddresses?: boolean;
}

/**
 * Multi-row site details form (Type, Alias, NPI, Name, Address, City, State, Zip).
 * Internal type shows only Type. External type shows the location fields.
 * Changing NPI replaces Name and the address fields from the matched location.
 * Changing Alias overwrites the other fields when that alias's location has
 * a value. Changing Address fills City, State, and Zip from that street's
 * entry on the selected NPI. Changing Name replaces NPI and the address fields.
 *
 * @param props - Controlled values, change handler, and optional options.
 * @returns Stacked searchable dropdown rows.
 */
const SiteDetailsForm = ({
  values,
  onChange,
  options = {},
  locations = [],
  loading = false,
  hasMultipleAddresses = false,
}: SiteDetailsFormProps) => {
  const isInternal = isInternalSiteType(values.type);
  const visibleFields = isInternal
    ? SITE_DETAILS_FIELDS.filter((field) => field.key === "type")
    : SITE_DETAILS_FIELDS;
  const handleFieldChange =
    (field: keyof SiteDetailsFormValues) =>
    (value: string, _option: SearchableCardOption) => {
      onChange(applySiteDetailsAutoPopulate(values, field, value, locations));
    };

  return (
    <div className="site-details-form w-full">
      {hasMultipleAddresses && !isInternal && (
        <div className="multiple-address-warning flex gap-2 rounded-md border border-secondaryYellow-3 bg-secondaryYellow-11 p-3">
          <div className="warning-icon flex items-center justify-center">
            <WarningIcon height="16" width="16" />
          </div>
          <div className="warning-text text-xs">
            This site has multiple addresses. Please select the correct address.
          </div>
        </div>
      )}
      {visibleFields.map((field) => {
        const fieldValue = blankIfMissingSiteDetailsPlaceholder(
          values[field.key],
        );
        const placeholder =
          ADDRESS_DISPLAY_FIELDS.has(field.key) && !fieldValue
            ? SITE_DETAILS_MISSING_FIELD_PLACEHOLDER
            : field.placeholder;

        return (
          <SingleInputField
            key={field.key}
            id={`site_details_${field.key}`}
            label={field.label}
            value={fieldValue}
            options={options[field.key] ?? []}
            placeholder={placeholder}
            searchPlaceholder={field.searchPlaceholder}
            required={field.required}
            error={getSiteDetailsFieldError(field.key, fieldValue)}
            loading={field.key === "type" ? false : loading}
            loadingMessage="Loading locations..."
            onChange={handleFieldChange(field.key)}
          />
        );
      })}
    </div>
  );
};

export default SiteDetailsForm;
